import { supabase } from '@/integrations/supabase/client';
import { TeamMember, UserRole } from '@/types/team';
import { generateRegistrationCode } from '@/utils/registrationCode';
import { TeamMemberCreateSchema, TeamMemberUpdateSchema, validateData, safeValidate } from '@/lib/validation/schemas';
import { calculateExperience } from '@/utils/experienceCalculator';

export class TeamService {
  // Create a new team member with registration code (by admin)
  static async createTeamMember(member: Partial<TeamMember>): Promise<TeamMember & { registrationCode?: string }> {
    console.log('🔍 Creating team member with data:', member);
    
    try {
      // Validate input data
      const validation = safeValidate(TeamMemberCreateSchema, {
        name: member.name,
        email: member.email,
        position: member.position,
        role: member.role,
        department: member.department,
        phone: member.phone,
        skills: member.skills,
        experience: member.experience,
        reportTo: member.reportTo,
        imageUrl: member.imageUrl,
        siteOfficeId: member.siteOfficeId,
      });
      
      if (!validation.success) {
        throw new Error(`Validation failed: ${(validation as { success: false; error: string }).error}`);
      }
      
      // Generate unique registration code
      const registrationCode = generateRegistrationCode();
      
      const insertData: Record<string, any> = {
        name: member.name || '',
        email: member.email || '',
        position: member.position || '',
        role: member.role || 'viewer',
        department: member.department || '',
        phone: member.phone || '',
        status: 'inactive', // New members start as inactive until approved
        join_date: member.joinDate || new Date().toISOString(),
        projects_assigned: member.projectsAssigned || 0,
        skills: member.skills || [],
        experience: member.experience || '',
        report_to: member.reportTo || null,
        image_url: member.imageUrl || '',
        site_office_id: member.siteOfficeId || null, // null = All Site Office (akses semua)
        // Approval workflow fields
        approval_status: 'pending',
        registration_code: registrationCode,
      };
      
      console.log('📝 Inserting member data with registration code:', insertData);
      
      const { data, error } = await supabase
        .from('team_members')
        .insert(insertData as any)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error creating team member:', error);
        throw new Error(`Failed to create team member: ${error.message}`);
      }

      console.log('✅ Team member created successfully:', data);
      const mappedMember = this.mapDatabaseToTeamMember(data);
      
      
      return { ...mappedMember, registrationCode };
    } catch (err) {
      console.error('❌ Exception creating team member:', err);
      throw new Error(`Failed to create team member: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  // Get all team members - uses RPC to bypass heavy RLS for performance
  static async getTeamMembers(): Promise<TeamMember[]> {
    try {
      // First try fast RPC (SECURITY DEFINER, no RLS overhead)
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_team_members_for_display');

      if (!rpcError && rpcData) {
        return (rpcData as any[]).map(row => this.mapDatabaseToTeamMember({
          id: row.member_id,
          name: row.member_name,
          email: row.member_email,
          position: row.member_position,
          role: row.member_role,
          department: row.member_department,
          phone: row.member_phone,
          status: row.member_status,
          image_url: row.member_image_url,
          site_office_id: row.member_site_office_id,
          // defaults for fields not in the RPC
          approval_status: 'approved',
          is_admin: row.member_role === 'administrator',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          projects_assigned: 0,
          is_blocked: false,
        }));
      }

      // RPC failed – log and return empty instead of falling back to 
      // direct team_members query which triggers heavy RLS and causes timeouts
      console.warn('⚠️ RPC get_team_members_for_display failed:', rpcError?.message);
      return [];
    } catch (err) {
      console.error('❌ Error fetching team members:', err);
      return [];
    }
  }

  // Get a single team member by ID with full data
  static async getTeamMemberById(id: string): Promise<TeamMember | null> {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !data) {
        console.error('❌ Error fetching team member by ID:', error);
        return null;
      }

      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Error fetching team member by ID:', err);
      return null;
    }
  }

  // Update team member
  static async updateTeamMember(id: string, memberData: TeamMember): Promise<TeamMember> {
    console.log('🔍 Updating team member:', id, memberData);
    
    try {
      // Validate input data
      const validation = safeValidate(TeamMemberUpdateSchema, {
        name: memberData.name,
        email: memberData.email,
        position: memberData.position,
        role: memberData.role,
        department: memberData.department,
        phone: memberData.phone,
        skills: memberData.skills,
        experience: memberData.experience,
        reportTo: memberData.reportTo,
        imageUrl: memberData.imageUrl,
      });
      
      if (!validation.success) {
        throw new Error(`Validation failed: ${(validation as { success: false; error: string }).error}`);
      }
      
      const updateData = {
        name: memberData.name,
        email: memberData.email,
        position: memberData.position,
        role: memberData.role,
        department: memberData.department,
        phone: memberData.phone,
        status: memberData.status,
        join_date: memberData.joinDate,
        projects_assigned: memberData.projectsAssigned,
        skills: memberData.skills,
        // Experience is calculated automatically from join_date, no need to store it
        report_to: memberData.reportTo || null,
        image_url: memberData.imageUrl || '',
        updated_at: new Date().toISOString(),
      };
      
      console.log('📝 Update data being sent to database:', updateData);

      const { data, error } = await supabase
        .from('team_members')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error updating team member:', error);
        throw new Error(`Failed to update team member: ${error.message}`);
      }

      console.log('✅ Team member updated successfully:', data);
      
      
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to update team member:', err);
      throw err;
    }
  }

  // Delete team member
  static async deleteTeamMember(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting team member:', id);
      
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Supabase error deleting team member:', error);
        throw new Error(`Failed to delete team member: ${error.message}`);
      }

      console.log('✅ Team member deleted successfully');
      
      
    } catch (err) {
      console.error('❌ Error deleting team member:', err);
      throw new Error(`Failed to delete team member: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  // Find team member by email
  static async findTeamMemberByEmail(email: string): Promise<TeamMember | null> {
    try {
      console.log('🔍 Finding team member by email:', email);
      
      // Use specific columns to avoid transferring large base64 image_url data
      const { data, error } = await supabase
        .from('team_members')
        .select('id,name,email,position,role,department,phone,status,join_date,projects_assigned,skills,experience,report_to,image_url,site_office_id,approval_status,approved_by,approved_at,registration_code,is_blocked,blocked_at,blocked_by,block_reason,is_admin,created_at,updated_at')
        .eq('email', email)
        .maybeSingle();

      if (error) {
        console.error('❌ Supabase error finding team member:', error);
        return null;
      }

      if (!data) {
        console.log('⚠️ No team member found with email:', email);
        return null;
      }

      console.log('✅ Team member found:', data);
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Error finding team member by email:', err);
      return null;
    }
  }

  // Initialize default admin
  static async initializeDefaultAdmin(): Promise<TeamMember | null> {
    try {
      console.log('🔍 TeamService: initializeDefaultAdmin called');
      
      // Check if any admin exists
      const { data: existingAdmins, error: checkError } = await supabase
        .from('team_members')
        .select('id,name,email,position,role,department,phone,status,join_date,projects_assigned,skills,experience,report_to,site_office_id,approval_status,is_admin,created_at,updated_at')
        .eq('role', 'administrator')
        .limit(1);

      if (checkError) {
        console.error('❌ Error checking for existing admin:', checkError);
        return null;
      }

      if (existingAdmins && existingAdmins.length > 0) {
        console.log('✅ Admin already exists');
        return this.mapDatabaseToTeamMember(existingAdmins[0]);
      }

      console.log('⚠️ No admin found, you may need to add one manually');
      return null;
    } catch (err) {
      console.error('❌ TeamService: Error initializing default admin:', err);
      return null;
    }
  }

  // Approve team member
  static async approveTeamMember(id: string, approvedById: string): Promise<TeamMember> {
    try {
      console.log('🔍 Approving team member:', id);
      
      const updateData: Record<string, any> = {
        status: 'active',
        approval_status: 'approved',
        approved_by: approvedById,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('team_members')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error approving team member:', error);
        throw new Error(`Failed to approve team member: ${error.message}`);
      }

      console.log('✅ Team member approved successfully');
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to approve team member:', err);
      throw err;
    }
  }

  // Reject team member
  static async rejectTeamMember(id: string, rejectedById: string): Promise<TeamMember> {
    try {
      console.log('🔍 Rejecting team member:', id);
      
      const updateData: Record<string, any> = {
        status: 'inactive',
        approval_status: 'rejected',
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('team_members')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error rejecting team member:', error);
        throw new Error(`Failed to reject team member: ${error.message}`);
      }

      console.log('✅ Team member rejected successfully');
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to reject team member:', err);
      throw err;
    }
  }

  // Find member by registration code
  static async findMemberByRegistrationCode(code: string): Promise<TeamMember | null> {
    try {
      console.log('🔍 Finding member by registration code:', code);
      
      // Use raw query to avoid TypeScript deep instantiation issues
      const { data, error } = await (supabase
        .from('team_members')
        .select('id,name,email,position,role,department,phone,status,join_date,projects_assigned,skills,experience,report_to,image_url,site_office_id,approval_status,approved_by,approved_at,registration_code,is_blocked,blocked_at,blocked_by,block_reason,is_admin,created_at,updated_at') as any)
        .eq('registration_code', code.toUpperCase())
        .maybeSingle();

      if (error) {
        console.error('❌ Error finding member by code:', error);
        return null;
      }

      if (!data) {
        console.log('⚠️ No member found with code:', code);
        return null;
      }

      console.log('✅ Member found by code:', data);
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Error finding member by registration code:', err);
      return null;
    }
  }

  // Deprecated: Password management now handled by Supabase Auth
  // This method is kept for backwards compatibility but no longer stores passwords in team_members
  static async setMemberPassword(email: string, password: string): Promise<TeamMember | null> {
    console.log('⚠️ setMemberPassword is deprecated - passwords are now managed by Supabase Auth');
    // Return the member without modifying password (column no longer exists)
    return this.findTeamMemberByEmail(email);
  }

  // Get pending members (for admin approval panel)
  static async getPendingMembers(): Promise<TeamMember[]> {
    try {
      console.log('🔍 Fetching pending members...');
      
      // Use raw query to avoid TypeScript deep instantiation issues
      // Note: password column has been removed - check approval_status only
      const { data, error } = await (supabase
        .from('team_members')
        .select('id,name,email,position,role,department,phone,status,join_date,projects_assigned,skills,experience,report_to,image_url,site_office_id,approval_status,approved_by,approved_at,registration_code,is_blocked,blocked_at,blocked_by,block_reason,is_admin,created_at,updated_at') as any)
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching pending members:', error);
        return [];
      }

      console.log('✅ Pending members fetched:', data?.length || 0);
      return (data || []).map((row: any) => this.mapDatabaseToTeamMember(row));
    } catch (err) {
      console.error('❌ Error getting pending members:', err);
      return [];
    }
  }

  // Map database row to TeamMember interface
  private static mapDatabaseToTeamMember(data: any): TeamMember {
    const joinDate = data.join_date || data.created_at;
    return {
      id: data.id,
      name: data.name || '',
      email: data.email || '',
      position: data.position || '',
      role: (data.role || 'viewer') as UserRole,
      department: data.department || '',
      phone: data.phone || '',
      status: data.status || 'inactive',
      joinDate: joinDate,
      projectsAssigned: data.projects_assigned || 0,
      skills: data.skills || [],
      // Calculate experience automatically from join date
      experience: calculateExperience(joinDate),
      reportTo: data.report_to || undefined,
      imageUrl: data.image_url || '',
      siteOfficeId: data.site_office_id || null, // null = All Site Office
      approvalStatus: (data.approval_status || 'pending') as 'pending' | 'approved' | 'rejected',
      // Password fields removed - authentication now handled by Supabase Auth
      approvedBy: data.approved_by,
      approvedAt: data.approved_at,
      registrationCode: data.registration_code,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      tasks: [],
      // Block fields
      isBlocked: data.is_blocked || false,
      blockedAt: data.blocked_at,
      blockedBy: data.blocked_by,
      blockReason: data.block_reason,
    };
  }

  // Block a team member
  static async blockMember(id: string, blockedBy: string, reason: string): Promise<TeamMember> {
    try {
      console.log('🔍 Blocking team member:', id);
      
      const updateData: Record<string, any> = {
        is_blocked: true,
        blocked_at: new Date().toISOString(),
        blocked_by: blockedBy,
        block_reason: reason,
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('team_members')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error blocking team member:', error);
        throw new Error(`Failed to block team member: ${error.message}`);
      }

      console.log('✅ Team member blocked successfully');
      
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to block team member:', err);
      throw err;
    }
  }

  // Unblock a team member
  static async unblockMember(id: string): Promise<TeamMember> {
    try {
      console.log('🔍 Unblocking team member:', id);
      
      const updateData: Record<string, any> = {
        is_blocked: false,
        blocked_at: null,
        blocked_by: null,
        block_reason: null,
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('team_members')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error unblocking team member:', error);
        throw new Error(`Failed to unblock team member: ${error.message}`);
      }

      console.log('✅ Team member unblocked successfully');
      
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to unblock team member:', err);
      throw err;
    }
  }

  // Update admin status
  static async updateAdminStatus(email: string, isAdmin: boolean): Promise<TeamMember | null> {
    try {
      console.log('🔍 Updating admin status for:', email, 'to:', isAdmin);
      
      const role = isAdmin ? 'administrator' : 'viewer';
      
      const { data, error } = await supabase
        .from('team_members')
        .update({ role, updated_at: new Date().toISOString() })
        .eq('email', email)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error updating admin status:', error);
        return null;
      }

      console.log('✅ Admin status updated successfully');
      return this.mapDatabaseToTeamMember(data);
    } catch (err) {
      console.error('❌ Failed to update admin status:', err);
      return null;
    }
  }
}
